import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type Contact } from 'entities/Contacts/model/Contact'
import {
  type IIntegrationKey,
  type IResponseCalendlyIntegrationMeeting,
  type IResponseIntegrationMeeting,
  type Integration,
  IntegrationKey,
  IntegrationsApi,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export type MeetingsProps = {
  contact?: Contact | null
  integration?: Integration | null
  onClose?: () => void
  onAddMeetingLink?: (link: string) => void
  onNecessaryReconnect?: (status: boolean) => void
  integrations?: Integration[]
}

export type IIntegrationMeeting = IResponseIntegrationMeeting | IResponseCalendlyIntegrationMeeting

export class MeetingsStore {
  private _contact: Contact | null

  constructor({ contact }: MeetingsProps, private _entities: IEntitiesStore) {
    this._contact = contact || null

    makeAutoObservable(this)
  }

  private _search = ''
  private _loading = false
  private _meetings: IIntegrationMeeting[] | null = null
  private _selectedMeeting: IIntegrationMeeting | null = null
  private _selectedIntegration: Integration | null = null
  private _isNecessaryReconnect = false
  private _necessaryReconnectIntegrationKey: IIntegrationKey | undefined = undefined

  loadMeetings = async (integrationKey?: IIntegrationKey) => {
    try {
      this._loading = true
      const params = {
        limit: 100,
        is_active: true,
      }

      if (this.isCalendlySelectedIntegration || integrationKey === IntegrationKey.calendly) {
        const { data } = await IntegrationsApi.getCalendlyIntegrationMeetings(
          IntegrationKey.calendly,
          params
        )
        if (data.items) {
          this.setMeetings(data.items)
        }
      } else {
        const { data } = await IntegrationsApi.getIntegrationMeetings(
          this.selectedIntegration?.key || IntegrationKey.hubspot
        )
        if (data) {
          if (data.data) this.setMeetings(data.data)
          if (data.error) {
            this.setNecessaryReconnect()
          }
        }
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        toastStore.add({
          title: e.errors.getError('message'),
          type: 'error',
        })
      }
      logger.error(e)
      this.setNecessaryReconnect()
    } finally {
      this._loading = false
    }
  }

  setMeetings = (meetings: IIntegrationMeeting[]) => {
    this._meetings = meetings
  }

  setSearch = async (search: string) => {
    this._search = search
  }

  onSelectMeeting = (meeting: IIntegrationMeeting) => {
    this._selectedMeeting = meeting
    if (this.isCalendlySelectedIntegration) {
      this._entities.eventLogStore.logEvent(
        'Calendly Used',
        {
          event_id: 'calendly_used',
          action: 'Link Added',
          agencyId: this._entities.organizationStore.agency_id,
        },
        { groupId: this._entities.organizationStore.id }
      )
    }
  }

  get isCalendlySelectedIntegration() {
    return this.selectedIntegration?.key === IntegrationKey.calendly
  }

  onBack = () => {
    this._selectedMeeting = null
    this._selectedIntegration = null
    this._search = ''
  }

  onSelectedIntegration = (integration: Integration) => {
    this._selectedIntegration = integration
    this.loadMeetings()
  }

  setNecessaryReconnect = () => {
    this._isNecessaryReconnect = true
    if (this.selectedIntegration?.key) {
      this._necessaryReconnectIntegrationKey = this.selectedIntegration.key
    }
  }

  get contact() {
    return this._contact
  }

  get isShowBack() {
    return this.selectedMeeting || this.selectedIntegration
  }

  get filteredMeetings() {
    if (this._search) {
      return this._meetings?.filter((meeting) =>
        meeting.name?.toLowerCase().includes(this._search.toLowerCase())
      )
    }

    return this._meetings
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get selectedMeeting() {
    return this._selectedMeeting
  }

  get selectedIntegration() {
    return this._selectedIntegration
  }
  get isNecessaryReconnect() {
    return this._isNecessaryReconnect
  }

  get necessaryReconnectIntegrationKey() {
    return this._necessaryReconnectIntegrationKey
  }

  get isCurrentIntegrationNeedReconnect() {
    return this.selectedIntegration?.key === this._necessaryReconnectIntegrationKey
  }
}
